import gql from 'graphql-tag';
import produce from 'immer';
import { updateMultiListQueryString } from './updateQueryString';
////////////////////////////////////////////////////////////////////////////////
// Queries
////////////////////////////////////////////////////////////////////////////////
export const isMultiFilterActive = parent => {
    return (searchParams) => {
        const query = __IN_SERVER__ ? searchParams : document.location.search;
        // If the search params include the parent id, this filter is applied
        return query === null || query === void 0 ? void 0 : query.includes(parent.id);
    };
};
////////////////////////////////////////////////////////////////////////////////
// Mutations
////////////////////////////////////////////////////////////////////////////////
const multiListFilterFragment = gql `
  fragment GetMultiListFilter on MultiListFilter {
    isActive @client
    id
    lists {
      id
      hasActiveItems @client
      items {
        id
        value
        isActive @client
      }
    }
  }
`;
export const setMultiListFilter = (_, { values, id }, { cache, getCacheKey }) => {
    const fragmentId = getCacheKey({
        id,
        __typename: 'MultiListFilter'
    });
    const multiListFilter = cache.readFragment({
        fragment: multiListFilterFragment,
        id: fragmentId
    });
    const data = produce(multiListFilter, draft => {
        draft.isActive = true;
        values.forEach((val, index) => {
            draft.lists[index].hasActiveItems = true;
            draft.lists[index].items.find(item => item.value === val).isActive = true;
        });
    });
    cache.writeData({
        id: fragmentId,
        data
    });
    const { activeFilters } = updateActiveMultiListFilters(cache, data);
    updateMultiListQueryString(activeFilters);
    return data;
};
////////////////////////////////////////////////////////////////////////////////
export const toggleMultiListFilterItem = (_, { value, filter, listIndex }, { cache, getCacheKey }) => {
    const fragmentId = getCacheKey({
        id: filter.id,
        __typename: 'MultiListFilter'
    });
    const multiListFilter = cache.readFragment({
        fragment: multiListFilterFragment,
        id: fragmentId
    });
    const data = produce(multiListFilter, draft => {
        const totalLists = draft.lists.length;
        // Update this list
        const list = draft.lists[listIndex];
        let isNowActive = true;
        if (value === null) {
            // Clear this list
            list.items.forEach(item => (item.isActive = false));
            isNowActive = false;
        }
        else {
            list.items.forEach(item => {
                if (item.value === value) {
                    const itemCurrentlyActive = item.isActive;
                    // If the item is already active, we are toggling it off
                    if (itemCurrentlyActive)
                        isNowActive = false;
                    item.isActive = !item.isActive;
                }
                else {
                    item.isActive = false;
                }
            });
        }
        list.hasActiveItems = isNowActive;
        // De-select values from other lists
        if (listIndex + 1 < totalLists) {
            draft.lists.forEach((list, index) => {
                if (index <= listIndex)
                    return;
                if (!list.hasActiveItems)
                    return;
                list.hasActiveItems = false;
                list.items.forEach(item => (item.isActive = false));
            });
        }
        if (listIndex === 0 && !isNowActive) {
            // If this is the first list, and we just set the item to inactive, the
            // whole filter should be set to inactive
            draft.isActive = false;
        }
        else {
            draft.isActive = true;
        }
    });
    cache.writeData({
        id: fragmentId,
        data
    });
    const { activeFilters } = updateActiveMultiListFilters(cache, data);
    updateMultiListQueryString(activeFilters);
    return data;
};
/////////////////////////////////////////////////////////////////////////////////
export const clearMultiListFilter = (_, { id }, { cache, getCacheKey }) => {
    const fragmentId = getCacheKey({ id, __typename: 'MultiListFilter' });
    const filter = cache.readFragment({
        id: fragmentId,
        fragment: multiListFilterFragment
    });
    const data = produce(filter, draft => {
        draft.isActive = false;
        draft.lists.forEach(list => {
            if (!list.hasActiveItems)
                return;
            list.hasActiveItems = false;
            list.items.forEach(item => (item.isActive = false));
        });
    });
    cache.writeData({
        id: fragmentId,
        data
    });
    const { activeFilters } = updateActiveMultiListFilters(cache, data);
    window.location.search && updateMultiListQueryString(activeFilters);
    return data;
};
////////////////////////////////////////////////////////////////////////////////
/**
 * Update active multi list filters in client cache
 * @param cache Apollo Cache
 * @param data The multi list filter that has been modified
 */
function updateActiveMultiListFilters(cache, data) {
    const activeMultiListFilterQuery = gql `
    query GetActiveMultiListFilters {
      activeFilters @client {
        multiListFilters {
          id
          values
        }
      }
    }
  `;
    const currentActiveFilters = cache.readQuery({
        query: activeMultiListFilterQuery
    });
    const activeFilters = produce(currentActiveFilters, draft => {
        // Check if the filter that was just toggled is already in activeFilters
        const existingListFilter = draft.activeFilters.multiListFilters.find(filter => filter.id === data.id);
        // Get an array of ListFilterItem values
        const lists = data.lists.filter(list => list.hasActiveItems);
        const listItems = lists.flatMap(list => list.items);
        const values = listItems
            .filter(listItem => listItem.isActive)
            .map(item => item.value);
        if (existingListFilter && !data.isActive) {
            // The multi filter has been cleared, so remove it from active filters
            draft.activeFilters.multiListFilters = draft.activeFilters.multiListFilters.filter(filter => filter.id !== data.id);
            return;
        }
        if (existingListFilter && data.isActive) {
            // Update the existing filter with the new values
            existingListFilter.values = values;
            return;
        }
        if (!existingListFilter) {
            // Create a new entry in active filters
            draft.activeFilters.multiListFilters.push({
                id: data.id,
                values,
                __typename: 'MultiListFilterInput'
            });
        }
    });
    cache.writeData({
        data: activeFilters
    });
    return activeFilters;
}
//# sourceMappingURL=multiListFilter.js.map